import type { Utility } from "@cs2/contracts";

/**
 * Die Granaten-Symbole als reine Pfaddaten — bewusst ohne JSX, damit beide
 * Rendering-Stacks sie benutzen können: das Werkzeugmenü und die Quickview als
 * SVG-`<path>`, der Editor als Konva-`<Path>`. Ein zweiter Satz Symbole pro
 * Stack wäre der Anfang der Divergenz (Hard Rule 2).
 */
export interface GrenadePath {
  d: string;
  opacity?: number;
  fillRule?: "evenodd";
}

export interface GrenadeGlyph {
  /** Kantenlänge des quadratischen Quellrasters — Skalierung = Zielgröße / viewBox. */
  viewBox: number;
  paths: GrenadePath[];
}

const GRENADE_BODY: GrenadePath = {
  d: "M9.744 6.092V5.084c-.48-.05-.721-.243-.721-.58 0-.218.09-.361.27-.428L9.894 4h3.309L16 7.754v7.937l-1.414.58-.45-.076c-.1-.151-.061-.294.119-.428.16-.119.402-.252.723-.404l.42-.177V8.107l-1.203-.58v1.185l-.722.554v1.185h-.301v1.209h.3v1.26h-.3v1.21h.3v1.26h-.3v1.209h.3v1.159l.723 1.033v.857c0 .235-.14.352-.42.352H8.42c-.282-.001-.421-.118-.421-.353v-.857l.691-1.033v-1.16h.331v-1.209h-.33v-1.26h.33V12.92h-.33v-1.26h.33v-1.208h-.33V9.266L8 8.712V7.1c0-.235.14-.353.42-.353h.422l.902-.655z",
};

export const grenadeGlyphs: Record<Utility["type"], GrenadeGlyph> = {
  SMOKE: {
    viewBox: 24,
    paths: [
      {
        d: "M10.073 6.72l-1.535.287-.216.244h5.99-5.988V19.58h-.002c.822.53 1.863.847 2.995.847 1.132 0 2.172-.317 2.995-.847V7.63l.912 1.508v8.673l-.194.797h.44l.208-.797V8.758l-2.062-5.185h-.273l-.428.633-.334-.135h-2.508l-.451 1.031h.451V6.72zm-1.447 9.45l2.719.575 2.718-.574v1.013l-2.718.575-2.72-.575v-1.013zm2.719-6.485l-2.72-.574v1.014l2.72.574 2.718-.574V9.11l-2.718.574z",
        fillRule: "evenodd",
      },
    ],
  },
  MOLLY: {
    viewBox: 24,
    paths: [
      {
        d: "M18.043 7.208c-.052.119-.01.263.131.432.122.17.088.397-.105.686l-.842.965c-.197.219-.48.356-.836.42l.24-.379.12-.024.912-1.203-.175-.44-.631-.583a3.1 3.1 0 00-.682-.355l-.552-.047-.862 1.138-.234.344h.097l-.584.684-.334-.647c-.14-.457-.166-.829-.078-1.118l.315-.558c.121-.236.121-.558 0-.965l.29.102c.227.118.393.305.497.56l.08.685.026.279.394-.838c.017-.17-.096-.432-.342-.789l-.577-.71c-.105-.153-.148-.508-.131-1.068.017-.574.105-.938.263-1.09.035.355.192.668.472.939.14.136.517.288 1.13.457.246.068.394.212.446.432l.21.533.29.38c.122.154.219.196.289.128.07-.067 0-.262-.209-.584-.23-.337-.195-.711.103-1.117.036.17.246.423.631.762.385.356.622.704.71 1.042l.024.508-.21.61-.286.43zm-1.42 1.906l.79-1.041-.132-.33-.552-.483a2.9 2.9 0 00-.63-.33l-.367-.025-.788 1.04h.025v.153l-2.075 2.44-.472.304-.788.355c-.79.356-1.314.72-1.577 1.092l-4.574 6.222c-.07.169.027.456.29.863.297.441.692.83 1.182 1.169.491.338.967.565 1.524.71.557.145.946-.024.946-.024l4.571-6.198c.262-.372.447-.974.55-1.803.09-.846.168-1.312.238-1.396l1.707-2.693.133-.025z",
      },
    ],
  },
  FLASH: {
    viewBox: 24,
    paths: [
      {
        d: "M8.56 12.666l-.714.95 2.856.821 1.903-.537.85-.285L17.5 11.94l-1.393-1.044 2.175-1.77-1.02-.475 1.02-2.688-2.175.41 3.025-3.984-4.419 1.265L12.605.68 10.94 3.242l-3.638-1.55.544 3.574-4.385-.348 3.366 2.625-1.326.411 1.801 2.056-2.277.696 2.277 1.233 1.258.727z",
        opacity: 0.3,
      },
      GRENADE_BODY,
    ],
  },
  HE: {
    viewBox: 24,
    paths: [
      {
        d: "M17.962 16.728l-.89-5.691-2.908-2.763H9.105a.3.3 0 01-.3-.3V5.433h-.63l.592-1.903h5.557v-.006l1.244 2.674 2.018 4.245.694 6.285h-.318zM9.529 8.274h3.629v1.254a5.624 5.624 0 11-3.628 0V8.274z",
        fillRule: "evenodd",
      },
    ],
  },
  C4: {
    viewBox: 32,
    paths: [
      {
        d: "M4.27 7.977a1 1 0 00-1 1V23a1 1 0 001 1h23.46a1 1 0 001-1V8.977a1 1 0 00-1-1H4.27zm3.599 11.328c.233.026.496.04.79.04.686 0 1.386-.074 2.1-.22v-1.18c-.834.193-1.48.29-1.94.29-1.074 0-1.61-.294-1.61-.88v-2.76c0-.367.156-.604.47-.71a1.68 1.68 0 01.42-.1c.14-.014.333-.02.58-.02.586 0 1.266.093 2.04.28v-1.17c-.86-.147-1.547-.22-2.06-.22-2.007 0-3.01.653-3.01 1.96v2.85c0 .32.07.596.21.83.133.233.3.416.5.55.2.126.453.23.76.31a5.3 5.3 0 00.75.15zm3.447-1.37h3v1.31h1.32v-1.31h.7v-1.05h-.7v-4.13h-1.65l-2.67 4.03v1.15zm3-3.8v2.75h-1.75l1.75-2.75zm4.415-.384h2.05V15.8h-2.05v-2.05zm4.478 0h-2.05V15.8h2.05v-2.05zm.38 0h2.048V15.8h-2.049v-2.05zm-2.809 2.426h-2.049v2.049h2.05v-2.05zm.38 0h2.049v2.049h-2.05v-2.05zm4.477 0h-2.049v2.049h2.049v-2.05z",
        fillRule: "evenodd",
      },
    ],
  },
  DECOY: {
    viewBox: 24,
    paths: [
      GRENADE_BODY,
      {
        d: "M17 3h1v1.134l.982-.567.5.866L18.5 5l.982.567-.5.866L18 5.866V7h-1V5.866l-.982.567-.5-.866L16.5 5l-.982-.567.5-.866.982.567V3zm1.482 5h1v1.134l.982-.567.5.866-.982.567.982.567-.5.866-.982-.567V12h-1v-1.134l-.982.567-.5-.866.982-.567L17 9.433l.5-.866.982.567V8z",
        opacity: 0.5,
        fillRule: "evenodd",
      },
    ],
  },
};

/**
 * Skalierung und Versatz, um ein Symbol zentriert in `size` Einheiten zu
 * zeichnen. Beide Stacks brauchen dieselbe Rechnung, sonst sitzen die Symbole
 * unterschiedlich auf der Scheibe.
 */
export function grenadeGlyphTransform(
  type: Utility["type"],
  size: number,
): { scale: number; offset: number } {
  const glyph = grenadeGlyphs[type];
  const scale = size / glyph.viewBox;
  return { scale, offset: -size / 2 };
}
